export function intelexCsrfToken() {
  const names = ["__Host-vwzz_csrf", "vwzz_csrf"];
  const cookies = document.cookie.split(";").map((item) => item.trim());
  for (const name of names) {
    const prefix = `${name}=`;
    const match = cookies.find((item) => item.startsWith(prefix));
    if (match) return decodeURIComponent(match.slice(prefix.length));
  }
  return "";
}

const intelexErrorMessages = {
  mandatory_response_note_required:
    "Add a message explaining what response the pilot must provide.",
  closed_report_cannot_require_response:
    "Reopen pilot replies before requiring a response.",
  report_conversation_closed: "Pilot responses are closed for this report.",
  invalid_report_image: "The selected file is not a valid JPEG or PNG image.",
  unsupported_report_image_type: "Only JPEG and PNG images can be uploaded.",
  too_many_report_uploads:
    "The image upload limit has been reached. Please try again later.",
  request_too_large: "The selected image is larger than the 5 MB limit.",
  report_image_not_found:
    "One of the uploaded images is no longer available. Upload it again.",
  invalid_report_message_attachment:
    "One of the attached images could not be added to this message.",
  too_many_report_images:
    "A report can contain no more than 20 uploaded images.",
  message_deletion_reason_required:
    "Enter a short reason before deleting this message.",
  message_deletion_not_allowed: "This message can no longer be deleted.",
  report_state_reason_required: "Enter a reason for this decision.",
  report_decision_reason_required:
    "Enter a reason before recording the final report decision.",
  invalid_report_review: "Select a valid report decision.",
  linked_pirep_review_required:
    "Use the Linked PIREP review controls so the decision remains synchronized with vAMSYS.",
  report_already_closed: "This report is already closed.",
  report_already_open: "This report is already open.",
  report_has_no_linked_pirep: "This report has no linked PIREP.",
  linked_pirep_verification_failed:
    "The linked PIREP or pilot could not be verified. No vAMSYS action was performed.",
  claim_pirep_moderation_not_supported:
    "Claim PIREPs must be moderated in vAMSYS directly.",
  pirep_action_reason_required: "Enter a reason before changing vAMSYS.",
  report_deletion_reason_required:
    "Enter a reason before deleting this report.",
  invalid_test_credentials: "The test username or password is incorrect.",
  test_login_disabled: "Local test sign-in is disabled on this server.",
  invalid_test_account:
    "Complete every test account field and use a password of at least 12 characters.",
  portal_username_in_use: "That username is already in use.",
  cannot_remove_own_admin_access:
    "You cannot remove your own administrator access.",
  cannot_revoke_own_access: "You cannot revoke your own access.",
  protected_admin_account:
    "This bootstrap administrator is protected by the server configuration.",
  test_account_role_locked:
    "Test accounts keep the test role and cannot be made administrators.",
  portal_user_must_be_revoked:
    "Revoke this account instead. Only unused local test accounts can be permanently deleted.",
  report_template_must_be_archived:
    "Archive this form before deleting it permanently.",
  use_accept_claim_endpoint:
    "Claim PIREPs must be accepted in vAMSYS directly.",
  use_reject_claim_endpoint:
    "Claim PIREPs must be rejected in vAMSYS directly.",
  landing_selection_required:
    "Select the correct touchdown in vAMSYS before accepting this PIREP.",
  pirep_already_accepted: "This PIREP is already accepted in vAMSYS.",
  pirep_already_rejected: "This PIREP is already rejected in vAMSYS.",
  pirep_already_invalidated: "This PIREP is already invalidated in vAMSYS.",
  claims_cannot_be_invalidated: "Claim PIREPs cannot be invalidated.",
  operations_api_error:
    "vAMSYS could not complete the action. No local decision was recorded.",
  invalid_exam:
    "Complete the exam title, instructions and configuration before saving.",
  invalid_exam_sections:
    "Every section needs a valid title and at least one fully configured question.",
  exam_cooldown:
    "This applicant is still within the configured retake waiting period.",
  exam_maximum_attempts:
    "The maximum number of exam attempts has been reached.",
  exam_retake_disabled: "Another exam attempt is not permitted.",
  exam_attempt_closed:
    "This exam attempt is already closed or its time has expired.",
  exam_attempt_deletion_reason_required:
    "Enter a reason before deleting this exam submission.",
  exam_attempt_in_progress:
    "An active exam attempt cannot be deleted. Cancel or allow it to finish first.",
  invalid_exam_answer: "This answer is not valid for the configured question.",
  required_exam_answers_missing:
    "Answer every required question before submitting the exam.",
  operations_api_not_configured:
    "vAMSYS registration approval is not configured on the server.",
  registration_identity_mismatch:
    "The vAMSYS registration did not match this authenticated applicant. No approval was performed.",
  registration_reactivation_not_available:
    "Registration reactivation is not available for this account.",
  entry_exam_not_passed:
    "A previously passed entry exam is required before reactivation.",
  pending_registration_not_found:
    "No pending vWZZ registration was found for your vAMSYS account.",
  active_registration_required:
    "Create a vWZZ registration on vAMSYS before starting the entry exam.",
};

function intelexErrorMessage(payload, fallback) {
  return (
    payload.message ||
    intelexErrorMessages[payload.error] ||
    payload.error ||
    fallback
  );
}

export async function intelexRequest(path, options = {}) {
  const method = options.method || "GET";
  const headers = { Accept: "application/json", ...(options.headers || {}) };
  if (options.body !== undefined) headers["Content-Type"] = "application/json";
  if (!["GET", "HEAD"].includes(method.toUpperCase()))
    headers["X-CSRF-Token"] = intelexCsrfToken();

  const response = await fetch(path, {
    credentials: "include",
    ...options,
    method,
    headers,
    body:
      options.body === undefined || typeof options.body === "string"
        ? options.body
        : JSON.stringify(options.body),
  });
  const payload = await response.json().catch(() => ({}));
  if (!response.ok) {
    const error = new Error(
      intelexErrorMessage(payload, `Request failed (${response.status}).`),
    );
    error.status = response.status;
    error.code = payload.error;
    error.details = payload;
    throw error;
  }
  return payload;
}

export async function intelexUploadImage(
  file,
  { purpose = "answer", reportId, fieldId } = {},
) {
  if (
    !(file instanceof File) ||
    !["image/jpeg", "image/png"].includes(file.type) ||
    file.size < 1 ||
    file.size > 5 * 1024 * 1024
  ) {
    throw new Error("Choose a JPEG or PNG image no larger than 5 MB.");
  }
  const params = new URLSearchParams({ purpose });
  if (reportId) params.set("report_id", String(reportId));
  if (fieldId) params.set("field_id", String(fieldId));
  const response = await fetch(`/api/portal/report-files?${params}`, {
    method: "POST",
    credentials: "include",
    headers: {
      Accept: "application/json",
      "Content-Type": file.type,
      "X-CSRF-Token": intelexCsrfToken(),
      "X-File-Name": encodeURIComponent(file.name || "image"),
    },
    body: file,
  });
  const payload = await response.json().catch(() => ({}));
  if (!response.ok) {
    const error = new Error(
      intelexErrorMessage(payload, `Upload failed (${response.status}).`),
    );
    error.status = response.status;
    error.code = payload.error;
    throw error;
  }
  return dataItem(payload);
}

export async function intelexDeleteImage(id) {
  return intelexRequest(`/api/portal/report-files/${encodeURIComponent(id)}`, {
    method: "DELETE",
  });
}

export function dataList(payload) {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.templates)) return payload.templates;
  if (Array.isArray(payload?.reports)) return payload.reports;
  return [];
}

export function dataItem(payload) {
  return payload?.data || payload?.template || payload?.report || payload;
}

export function displayDate(value, includeTime = true) {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return new Intl.DateTimeFormat(
    "en-GB",
    includeTime
      ? { dateStyle: "medium", timeStyle: "short", timeZone: "UTC" }
      : { dateStyle: "medium", timeZone: "UTC" },
  ).format(date);
}

export function airportCode(airport) {
  if (!airport) return "";
  if (typeof airport === "string") return airport;
  return airport.icao || airport.ident || airport.iata || airport.code || "";
}

export function vamsysPirepReviewUrl(value) {
  const id = String(value ?? "").trim();
  return /^[1-9]\d{0,18}$/.test(id)
    ? `https://vamsys.io/orwell/flight-centre/pireps/${id}/review`
    : "";
}

export function reportDetailPath(report) {
  const pirepId = String(report?.pirepId ?? "").trim();
  if (/^[1-9]\d{0,18}$/.test(pirepId))
    return `/interep/reports/pirep/${encodeURIComponent(pirepId)}`;

  const eventNumber = String(report?.eventNumber ?? "").trim();
  if (/^[A-Za-z0-9][A-Za-z0-9_-]{0,79}$/.test(eventNumber))
    return `/interep/reports/reference/${encodeURIComponent(eventNumber)}`;

  return `/interep/reports/${encodeURIComponent(report?.id ?? "")}`;
}
